import React, { useEffect, useState } from "react";
import { X, Loader2, Save, Trash2, Star } from "lucide-react";
import { toast } from "sonner";
import { api, SERVICE_OPTIONS } from "../../lib/site";

const empty = { name: "", location: "Halden", service: "", rating: 5, text: "", published: true };

export default function TestimonialEditor({ open, item, onClose, onSaved }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(item ? { ...empty, ...item } : empty); }, [item, open]);
  if (!open) return null;

  const save = async () => {
    setSaving(true);
    try {
      const payload = { name: form.name, location: form.location, service: form.service, rating: Number(form.rating) || 5, text: form.text, published: form.published };
      if (item?.id) await api.patch(`/admin/testimonials/${item.id}`, payload);
      else await api.post(`/admin/testimonials`, payload);
      toast.success(item?.id ? "Omtale oppdatert" : "Omtale lagt til");
      onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kunne ikke lagre"); }
    finally { setSaving(false); }
  };
  const del = async () => {
    if (!item?.id || !window.confirm("Slette omtalen?")) return;
    try { await api.delete(`/admin/testimonials/${item.id}`); toast.success("Slettet"); onSaved(); onClose(); }
    catch (e) { toast.error("Kunne ikke slette"); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/60 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-xl rounded-t-2xl sm:rounded-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sm:hidden pt-2 pb-1 grid place-items-center"><div className="h-1.5 w-10 rounded-full bg-[#D5D0C7]" /></div>
        <div className="p-4 sm:p-5 border-b border-[#E2DDD5] flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="font-display text-lg sm:text-xl font-bold">{item?.id ? "Rediger omtale" : "Ny omtale"}</h3>
          <button onClick={onClose} className="p-2 -m-2" data-testid="tst-close"><X size={20}/></button>
        </div>
        <div className="p-4 sm:p-5 space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block"><span className="text-[11px] uppercase tracking-wider text-[#555E68]">Kundens navn</span>
              <input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="mt-1 w-full rounded-md border border-[#D5D0C7] px-3 py-2.5 text-sm" data-testid="tst-name" />
            </label>
            <label className="block"><span className="text-[11px] uppercase tracking-wider text-[#555E68]">Sted</span>
              <input value={form.location} onChange={(e) => setForm({...form, location: e.target.value})} className="mt-1 w-full rounded-md border border-[#D5D0C7] px-3 py-2.5 text-sm" data-testid="tst-location" />
            </label>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block"><span className="text-[11px] uppercase tracking-wider text-[#555E68]">Tjeneste</span>
              <select value={form.service} onChange={(e) => setForm({...form, service: e.target.value})} className="mt-1 w-full rounded-md border border-[#D5D0C7] px-3 py-2.5 text-sm" data-testid="tst-service">
                <option value="">—</option>
                {SERVICE_OPTIONS.map(s => <option key={s}>{s}</option>)}
              </select>
            </label>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#555E68] mb-1.5">Stjerner</div>
              <div className="flex gap-1">
                {[1,2,3,4,5].map(n => (
                  <button key={n} type="button" onClick={() => setForm({...form, rating: n})} className="p-1" data-testid={`tst-star-${n}`}>
                    <Star size={22} className={n <= form.rating ? "text-[#E8B547] fill-[#E8B547]" : "text-[#D5D0C7]"} />
                  </button>
                ))}
              </div>
            </div>
          </div>
          <label className="block"><span className="text-[11px] uppercase tracking-wider text-[#555E68]">Omtale-tekst</span>
            <textarea rows={4} value={form.text} onChange={(e) => setForm({...form, text: e.target.value})} className="mt-1 w-full rounded-md border border-[#D5D0C7] px-3 py-2.5 text-sm" data-testid="tst-text" />
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={form.published} onChange={(e) => setForm({...form, published: e.target.checked})} data-testid="tst-published" />
            <span className="text-sm">Publisert på nettsiden</span>
          </label>
        </div>
        <div className="p-4 sm:p-5 border-t border-[#E2DDD5] flex items-center justify-between sticky bottom-0 bg-white">
          {item?.id ? <button onClick={del} className="text-[#8C2F19] font-semibold text-sm inline-flex items-center gap-1.5 hover:underline" data-testid="tst-delete"><Trash2 size={14}/> Slett</button> : <span/>}
          <button onClick={save} disabled={saving || !form.name || !form.text} className="btn-primary" data-testid="tst-save">{saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>} Lagre</button>
        </div>
      </div>
    </div>
  );
}
